// Owner Diagnostic Scoring Utilities
// Shared calculation logic for readiness scores and priority identification

// Question metadata from action_plan_seeds.rake
const QUESTION_CEPA_MAPPING = {
  'owner_q1': 'identify',   // Personal financial plan
  'owner_q2': 'identify',   // Net worth knowledge
  'owner_q3': 'identify',   // Post-exit goals
  'owner_q4': 'protect',    // Family alignment
  'owner_q5': 'identify',   // Emotional readiness
  'owner_q6': 'protect',    // Estate/tax planning
  'owner_q7': 'build',      // Revenue growth
  'owner_q8': 'build',      // Recurring revenue
  'owner_q9': 'manage',     // Management team
  'owner_q10': 'protect',   // Customer diversity
  'owner_q11': 'identify',  // Market position
  'owner_q12': 'manage',    // Systems & processes
};

const QUESTION_DOMAIN_MAPPING = {
  'owner_q1': 'personal',
  'owner_q2': 'personal',
  'owner_q3': 'personal',
  'owner_q4': 'personal',
  'owner_q5': 'personal',
  'owner_q6': 'personal',
  'owner_q7': 'business',
  'owner_q8': 'business',
  'owner_q9': 'business',
  'owner_q10': 'business',
  'owner_q11': 'business',
  'owner_q12': 'business',
};

const CATEGORY_LABELS = {
  identify: 'Personal & Financial Readiness',
  protect: 'Risk Mitigation & Protection',
  build: 'Building Value Drivers',
  manage: 'Operations & Management',
};

const CATEGORY_DESCRIPTIONS = {
  identify: 'Clarifying your personal goals, financial needs, and market positioning',
  protect: 'Reducing risks through estate planning, diversification, and continuity planning',
  build: 'Growing revenue, improving profitability, and increasing recurring revenue',
  manage: 'Strengthening operations, team independence, and documented processes',
};

/**
 * Calculate readiness scores from owner diagnostic answers
 * @param {Array<number>} ownerAnswers - 6 answers for owner readiness (1-6 scale)
 * @param {Array<number>} businessAnswers - 6 answers for business attractiveness (1-6 scale)
 * @returns {Object} { ownerReadinessScore, buyerAttractivenessScore, overallReadinessScore }
 */
export const calculateOwnerDiagnosticScores = (ownerAnswers, businessAnswers) => {
  const owner = Array.isArray(ownerAnswers) ? ownerAnswers : [];
  const business = Array.isArray(businessAnswers) ? businessAnswers : [];
  
  // Filter out null/zero values
  const ownerScores = owner.filter(v => v != null && v > 0);
  const businessScores = business.filter(v => v != null && v > 0);
  
  // Calculate percentages (1-6 scale → 0-100%)
  const ownerReadinessScore = ownerScores.length > 0 
    ? Math.round((ownerScores.reduce((a, b) => a + b, 0) / (ownerScores.length * 6)) * 100)
    : 0;
  
  const buyerAttractivenessScore = businessScores.length > 0
    ? Math.round((businessScores.reduce((a, b) => a + b, 0) / (businessScores.length * 6)) * 100)
    : 0;
  
  const overallReadinessScore = Math.round(
    (ownerReadinessScore + buyerAttractivenessScore) / 2
  );
  
  return {
    ownerReadinessScore,
    buyerAttractivenessScore,
    overallReadinessScore,
  };
};

/**
 * Identify top 2 priority areas based on assessment answers
 * Uses CEPA categories (identify, protect, build, manage) - excludes harvest for owner diagnostic
 * @param {Array<number>} ownerAnswers - 6 answers for owner readiness (1-6 scale)
 * @param {Array<number>} businessAnswers - 6 answers for business attractiveness (1-6 scale)
 * @returns {Array<Object>} Top 2 priority categories with metadata
 */
export const identifyTopPriorities = (ownerAnswers, businessAnswers) => {
  const owner = Array.isArray(ownerAnswers) ? ownerAnswers : [];
  const business = Array.isArray(businessAnswers) ? businessAnswers : [];
  const allAnswers = [...owner, ...business];
  
  // Map answers to questions with their metadata
  const questionScores = allAnswers.map((score, index) => {
    const questionKey = `owner_q${index + 1}`;
    return {
      questionKey,
      score: score || 0,
      cepaCategory: QUESTION_CEPA_MAPPING[questionKey],
      domain: QUESTION_DOMAIN_MAPPING[questionKey],
      isGap: score <= 4, // Threshold for task creation (matches ActionPlanBuilder.THRESHOLD)
      gapSeverity: score <= 4 ? (5 - score) : 0, // Lower score = higher severity (1=severe, 4=minor)
    };
  });
  
  // Group by CEPA category (only identify, protect, build, manage for owner diagnostic)
  const categories = ['identify', 'protect', 'build', 'manage'];
  const categoryAnalysis = categories.map(category => {
    const questionsInCategory = questionScores.filter(q => q.cepaCategory === category);
    const gaps = questionsInCategory.filter(q => q.isGap);
    const avgScore = questionsInCategory.length > 0
      ? questionsInCategory.reduce((sum, q) => sum + q.score, 0) / questionsInCategory.length
      : 6;
    const gapCount = gaps.length;
    const totalGapSeverity = gaps.reduce((sum, q) => sum + q.gapSeverity, 0);
    
    return {
      category,
      avgScore,
      gapCount,
      totalGapSeverity,
      // Priority score: combination of low average, number of gaps, and severity
      // Lower avg = worse, more gaps = worse, higher severity = worse
      priorityScore: (6 - avgScore) * 10 + gapCount * 5 + totalGapSeverity,
      questionsInCategory: questionsInCategory.length,
    };
  });
  
  // Sort by priority score (highest = most critical)
  const sortedCategories = categoryAnalysis
    .filter(c => c.gapCount > 0) // Only include categories with actual gaps
    .sort((a, b) => b.priorityScore - a.priorityScore);
  
  // Return top 2 (or less if fewer gaps exist)
  return sortedCategories.slice(0, 2).map(c => ({
    category: c.category,
    avgScore: Math.round(c.avgScore * 100 / 6), // Convert to percentage
    gapCount: c.gapCount,
    label: CATEGORY_LABELS[c.category],
    description: CATEGORY_DESCRIPTIONS[c.category],
  }));
};

/**
 * Get category label for display
 * @param {string} category - CEPA category (identify, protect, build, manage)
 * @returns {string} Display label
 */
export const getCategoryLabel = (category) => {
  return CATEGORY_LABELS[category] || category;
};

/**
 * Get category description for display
 * @param {string} category - CEPA category
 * @returns {string} Description text
 */
export const getCategoryDescription = (category) => {
  return CATEGORY_DESCRIPTIONS[category] || '';
};

export default calculateOwnerDiagnosticScores;

